import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [bookingId, setBookingId] = useState(null);
  const [paymentType, setPaymentType] = useState('full');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('error');
      setErrorMessage('No payment session found.');
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      
      if (response.data.status === 'paid' || response.data.status === 'deposit_paid') {
        setStatus('success');
        setBookingId(response.data.booking_id);
        setPaymentType(response.data.payment_type || 'full');
      } else if (response.data.status === 'expired' || response.data.status === 'invalid') {
        setStatus('expired');
        setErrorMessage(response.data.message || 'Payment session has expired.');
      } else {
        setStatus('pending');
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Failed to verify payment status.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center px-4 sm:px-6">
      <Card className="max-w-lg w-full">
        <CardHeader>
          <CardTitle className="text-center text-xl sm:text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>
            {status === 'loading' && 'Processing Payment...'}
            {status === 'success' && paymentType === 'deposit' && '✅ Deposit Received!'}
            {status === 'success' && paymentType !== 'deposit' && '✅ Payment Successful!'}
            {status === 'pending' && 'Payment Pending'}
            {status === 'expired' && '⏰ Session Expired'}
            {status === 'error' && 'Payment Error'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          {status === 'loading' && (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
            </div>
          )}
          
          {status === 'success' && paymentType === 'deposit' && (
            <div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-amber-800 font-medium">🎉 Your spot is reserved!</p>
                <p className="text-amber-700 text-sm mt-2">
                  Your 12% deposit has been received. Please note that the remaining balance must be paid before the program starts.
                </p>
              </div>
              <p className="text-slate-600 mb-6">You will receive a confirmation email with payment details shortly.</p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <Button onClick={() => navigate('/dashboard')} className="bg-emerald-700 hover:bg-emerald-800">View My Bookings</Button>
                <Button onClick={() => navigate('/programs')} variant="outline">Browse More Programs</Button>
              </div>
            </div>
          )}
          
          {status === 'success' && paymentType !== 'deposit' && (
            <div>
              <p className="text-slate-600 mb-6">Your booking has been confirmed! You will receive a confirmation email shortly.</p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <Button onClick={() => navigate('/dashboard')} className="bg-emerald-700 hover:bg-emerald-800">View My Bookings</Button>
                <Button onClick={() => navigate('/programs')} variant="outline">Browse More Programs</Button>
              </div>
            </div>
          )}
          
          {status === 'pending' && (
            <div>
              <p className="text-slate-600 mb-6">Your payment is being processed. Please check back shortly.</p>
              <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
            </div>
          )}
          
          {status === 'expired' && (
            <div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-amber-800 font-medium">Your payment session has expired</p>
                <p className="text-amber-700 text-sm mt-2">
                  Stripe checkout sessions expire after 24 hours. Please start a new booking to complete your payment.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <Button onClick={() => navigate('/programs')} className="bg-emerald-700 hover:bg-emerald-800">Browse Programs</Button>
                <Button onClick={() => navigate('/dashboard')} variant="outline">View Dashboard</Button>
              </div>
            </div>
          )}
          
          {status === 'error' && (
            <div>
              <p className="text-red-600 mb-4">{errorMessage || 'There was an error processing your payment.'}</p>
              <p className="text-slate-500 text-sm mb-6">If you believe this is an error, please contact support.</p>
              <Button onClick={() => navigate('/programs')}>Back to Programs</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
